import { isDevHost } from "./availability";
import { unwrapCompressedBody } from "@/auth/compression";
import type { ResolveTenantAppResponse } from "./types";

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "https://api.v3.metaship.ai";

/** Dev-only: remembers a `?tenantHost=` override for the rest of the tab's life. */
const DEV_TENANT_HOST_KEY = "metaship:dev:tenantHost";

function rememberDevTenantHost(host: string): void {
  try {
    sessionStorage.setItem(DEV_TENANT_HOST_KEY, host);
  } catch {
    // best-effort — the override just stops surviving a full-page load
  }
}

function recallDevTenantHost(): string | null {
  try {
    return sessionStorage.getItem(DEV_TENANT_HOST_KEY);
  } catch {
    return null;
  }
}

/**
 * The hostname to resolve against `GET /tenant-apps/resolve`. Production always
 * uses the real hostname the shell was served on. Dev overrides, checked in
 * order: `?tenantHost=` on the URL (per-tab, no rebuild) → the last
 * `?tenantHost=` this tab used → `VITE_TENANT_HOST` (per-checkout default) →
 * the real hostname (so a plain `localhost` still resolves — and fails cleanly
 * with `found:false` — instead of throwing).
 *
 * The sessionStorage step is what makes a local sign-in possible at all: Auth0
 * returns to `${origin}/auth0/callback` with ONLY its own query string, so a
 * `?tenantHost=` typed into the address bar is gone by the time the callback
 * boots. The shell then resolved plain `localhost`, got `found:false`, and
 * main.ts bailed before `Callback.vue` could exchange the code — a permanent
 * "Completing sign-in…" spinner.
 *
 * Every override is gated behind `isDevHost()` — a `?tenantHost=` query param
 * on a REAL tenant hostname must never let a visitor spoof a different
 * tenant's branding/modules on a live production domain.
 */
export function resolveTenantHost(location: Pick<Location, "search" | "hostname"> = window.location): string {
  const dev = isDevHost(location.hostname, import.meta.env.DEV);
  if (!dev) return location.hostname;
  const fromQuery = new URLSearchParams(location.search).get("tenantHost");
  if (fromQuery) {
    rememberDevTenantHost(fromQuery);
    return fromQuery;
  }
  const remembered = recallDevTenantHost();
  if (remembered) return remembered;
  const fromEnv = import.meta.env.VITE_TENANT_HOST;
  return fromEnv || location.hostname;
}

export async function fetchTenantApp(
  host: string,
  apiBase: string = API_BASE_URL,
): Promise<ResolveTenantAppResponse> {
  const res = await fetch(`${apiBase}/tenant-apps/resolve?host=${encodeURIComponent(host)}`, {
    headers: { "Time-Zone": Intl.DateTimeFormat().resolvedOptions().timeZone },
  });
  if (!res.ok) {
    throw new Error(`GET /tenant-apps/resolve failed: ${res.status}`);
  }
  // Guard against the Encore gzip envelope (not active on TenantApps today,
  // but one middleware addition away — see auth/compression.ts).
  return (await unwrapCompressedBody(await res.json())) as ResolveTenantAppResponse;
}
